import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { analyzeTrim, getCachedTrim, prewarmTrim } from '../utils/trimSilence';
import { BIBLE_BOOKS } from '../utils/bibleData';

const BOOK_ORDER_INDEX = new Map(BIBLE_BOOKS.map((b, i) => [b.id, i]));

// orderMode:
//  'verse' (default) — stable chronological order (book, then chapter, then
//    verse). Existing callers always pass a single book+chapter's worth of
//    recordings, so this is equivalent to the old verse-number-only sort for
//    them; the book/chapter comparison only matters for multi-passage input.
//  'sequence' — preserve the caller's exact declared order without
//    re-sorting at all (e.g. a Reading/Passages Plan's item sequence, which
//    may deliberately jump across chapters/books in a specific order).
const MultiAudioPlayer = forwardRef(({ recordings, orderMode = 'verse', autoPlayNewQueue = false, trimSilence = false, enhanceAudio = false, playbackRate = 1.0, onVersePlayback, onPlaybackStateChange, onLoadingStateChange, onQueueEnded }, ref) => {
  // Simplified state management
  const [isPlaying, setIsPlaying] = useState(false);

  // Live "trim silence" flag readable from inside the player object methods.
  const trimEnabledRef = useRef(false);

  // Web Audio API enhancement chain — set up at most once per component
  // lifetime, only if enhanceAudio is on, and only from inside play() (a
  // user gesture, with the element in a clean not-yet-playing state).
  const audioContextRef = useRef(null);
  const mediaSourceCreatedRef = useRef(false);
  const enhanceAudioRef = useRef(enhanceAudio);
  useEffect(() => { enhanceAudioRef.current = enhanceAudio; }, [enhanceAudio]);

  const rateRef = useRef(playbackRate);
  rateRef.current = playbackRate; // sync so play() always reads the latest rate on each verse start
  useEffect(() => {
    // Apply immediately to the live element when a verse is already playing.
    // Only touch a playing element — paused audio picks up the rate in play().
    const audio = versePlayerRef.current?.audio;
    if (audio && !audio.paused) audio.playbackRate = playbackRate;
  }, [playbackRate]);
  
  // Refs for the three-component system
  const verseQueueRef = useRef(null);
  const versePlayerRef = useRef(null);
  const verseHighlighterRef = useRef(null);
  
  // Audio element ref
  const audioRef = useRef(null);
  
  // Initialization flag to prevent multiple setups
  const hasInitializedRef = useRef(false);

  // VerseQueue System - Manages all verses and playback sequence (single-verse-per-file only)
  const createVerseQueue = () => ({
    allVerses: [],
    currentIndex: 0,
    totalCount: 0,
    remainingCount: 0,
    
    initialize: function(recordings) {
      // Process and deduplicate verses (single verse per file)
      const verseMap = new Map();
      
      recordings.forEach(recording => {
        const verseKey = `${recording.book}_${recording.chapter}_${recording.verse}`;
        const verseData = {
          id: recording.id,
          verseNumber: recording.verse,
          book: recording.book,
          chapter: recording.chapter,
          audioFile: recording.audioURL,
          name: recording.name,
          userId: recording.userId
        };
        
        // Only keep the first occurrence of each verse
        if (!verseMap.has(verseKey)) {
          verseMap.set(verseKey, verseData);
        }
      });

      // 'sequence' mode: keep the exact order the caller handed us (Map
      // preserves insertion order) — used for plan playback where the order
      // was deliberately built to match a plan's declared item sequence.
      // 'verse' mode (default): stable (book, chapter, verse) order.
      if (orderMode === 'sequence') {
        this.allVerses = Array.from(verseMap.values());
      } else {
        this.allVerses = Array.from(verseMap.values()).sort((a, b) => {
          const bookDiff = (BOOK_ORDER_INDEX.get(a.book) ?? 0) - (BOOK_ORDER_INDEX.get(b.book) ?? 0);
          if (bookDiff !== 0) return bookDiff;
          if (a.chapter !== b.chapter) return a.chapter - b.chapter;
          return a.verseNumber - b.verseNumber;
        });
      }

      this.totalCount = this.allVerses.length;
      this.remainingCount = this.allVerses.length;
      this.currentIndex = 0;
      
      // Optionally log verses elsewhere with queue report
    },
    
    logAllVerses: function() {
      console.log('📋 VERSE QUEUE: All verses in queue:');
      this.allVerses.forEach((verse, index) => {
        console.log(`  ${index + 1}. Verse ${verse.verseNumber} - ${verse.audioFile}`);
      });
    },
    
    getCurrentVerse: function() {
      return this.allVerses[this.currentIndex] || null;
    },
    
    getNextVerse: function() {
      return this.allVerses[this.currentIndex + 1] || null;
    },
    
    advanceToNext: function() {
      if (this.currentIndex < this.allVerses.length - 1) {
        this.currentIndex++;
        this.remainingCount = this.allVerses.length - this.currentIndex;
        return true;
      }
      return false;
    },
    
    generateQueueReport: function() {
      const currentVerse = this.getCurrentVerse();
      const nextVerse = this.getNextVerse();
      
      console.log('📋 QUEUE REPORT:');
      console.log(`a. The VerseQueue has determined there are ${this.totalCount} verses to play right now. Here is a list of the verses:`);
      
      this.allVerses.forEach((verse, index) => {
        console.log(`   verse ${verse.verseNumber} - ${verse.audioFile}`);
      });
      
      if (this.allVerses.length > 0) {
        const firstVerse = this.allVerses[0];
        console.log(`b. The first verse to play is ${firstVerse.book} ${firstVerse.chapter}:${firstVerse.verseNumber}.`);
      }
      
      if (currentVerse) {
        console.log(`c. Current Verse: ${currentVerse.book} ${currentVerse.chapter}:${currentVerse.verseNumber} is loaded into the versePlayer and it is highlighted. The audio file for current verse is ${currentVerse.audioFile}.`);
      }
      
      if (nextVerse) {
        console.log(`d. OnDeck: Next Verse ${nextVerse.book} ${nextVerse.chapter}:${nextVerse.verseNumber} and audio file for next verse is ${nextVerse.audioFile}`);
      } else {
        console.log('d. OnDeck: No next verse (this is the last verse)');
      }
      
      console.log('e. VersePlayer is aware of current verse, and I will send the onDeck verse to versePlayer when we reach the end time of current verse.');
      console.log('-----end queue report------');
    }
  });

  // VersePlayer System - Handles audio playback
  const createVersePlayer = () => ({
    audio: null,
    nextAudio: null, // Preload next verse
    currentVerse: null,
    nextVerse: null,
    isPlaying: false,
    trimEnd: null, // when trim-silence is on, stop the current verse at this time (seconds)
    
    initialize: function(audioElement) {
      this.audio = audioElement;
      // Required for createMediaElementSource() to work on these
      // cross-origin (Firebase Storage) URLs — without it, Web Audio treats
      // the output as tainted and silently renders silence (no error), even
      // though the underlying <audio> element still reports normal playback.
      // Must be set before playback starts capturing the element.
      this.audio.crossOrigin = 'anonymous';
      this.setupEventListeners();

      // Create a second audio element for preloading next verse
      this.nextAudio = document.createElement('audio');
      this.nextAudio.preload = 'metadata';
      this.nextAudio.crossOrigin = 'anonymous';
    },
    
    setupEventListeners: function() {
      if (!this.audio) return;
      
      console.log('🎵 VERSE PLAYER: Setting up event listeners...');
      console.log(`🎵 VERSE PLAYER: setupEventListeners - this object ID: ${this.constructor.name || 'anonymous'}`);
      console.log(`🎵 VERSE PLAYER: setupEventListeners - this.audio element: ${this.audio}`);
      
      // Store bound handlers to properly remove them
      this.boundHandlers = {
        loadedmetadata: this.handleLoadedMetadata.bind(this),
        canplay: this.handleCanPlay.bind(this),
        timeupdate: this.handleTimeUpdate.bind(this),
        ended: this.handleEnded.bind(this),
        error: this.handleError.bind(this)
      };
      
      console.log('🎵 VERSE PLAYER: boundHandlers created:', !!this.boundHandlers);
      console.log('🎵 VERSE PLAYER: timeupdate handler created:', !!this.boundHandlers.timeupdate);
      
      // Remove existing listeners to prevent duplicates
      if (this.audio.removeEventListener) {
        this.audio.removeEventListener('loadedmetadata', this.boundHandlers.loadedmetadata);
        this.audio.removeEventListener('canplay', this.boundHandlers.canplay);
        this.audio.removeEventListener('timeupdate', this.boundHandlers.timeupdate);
        this.audio.removeEventListener('ended', this.boundHandlers.ended);
      }
      
      // Add new listeners
      this.audio.addEventListener('loadedmetadata', this.boundHandlers.loadedmetadata);
      this.audio.addEventListener('canplay', this.boundHandlers.canplay);
      this.audio.addEventListener('timeupdate', this.boundHandlers.timeupdate);
      this.audio.addEventListener('ended', this.boundHandlers.ended);
      this.audio.addEventListener('error', this.boundHandlers.error);
      
      console.log('🎵 VERSE PLAYER: Event listeners set up for audio element');
      console.log('🎵 VERSE PLAYER: boundHandlers after setup:', !!this.boundHandlers);
    },
    
    handleLoadedMetadata: function() {
      if (!this.currentVerse) return;
      console.log(`🎵 VERSE PLAYER: Audio metadata loaded for verse ${this.currentVerse.verseNumber}`);
    },
    
    handleCanPlay: function() {
      if (!this.currentVerse) return;
      // Remove this console.log to prevent infinite spam
      // console.log(`🎵 VERSE PLAYER: Audio can play for verse ${this.currentVerse.verseNumber}`);
    },
    
    handleTimeUpdate: function() {
      if (!this.currentVerse || !this.audio) return;
      
      const currentTime = this.audio.currentTime;
      const endTime = this.audio.duration || 0;

      // When trim-silence is on, treat the spoken end (this.trimEnd) as the
      // effective end of the verse so we skip trailing dead air.
      const trimEnd = trimEnabledRef.current && this.trimEnd ? this.trimEnd : null;
      const effectiveEnd = trimEnd != null ? Math.min(trimEnd, endTime || trimEnd) : endTime;

      // Preload next verse when we're 60% through current verse (very early preloading)
      if (currentTime >= effectiveEnd * 0.6 && !this.nextVerse) {
        this.preloadNextVerse();
      }

      // Check if we've reached the end time (minimal buffer for near-seamless transition)
      if (effectiveEnd && currentTime >= effectiveEnd - 0.02) {
        // Use a flag to prevent multiple calls instead of removing listener
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        this.handleVerseEnd();
      }
    },
    
    handleEnded: function() {
      if (!this.currentVerse) return;
      console.log(`🎵 VERSE PLAYER: Audio ended for verse ${this.currentVerse.verseNumber}`);
      this.handleVerseEnd();
    },
    
    handleError: function(event) {
      console.error('🚨 VERSE PLAYER: Audio error:', event);
      console.error('🚨 VERSE PLAYER: Error details:', {
        error: this.audio.error,
        networkState: this.audio.networkState,
        readyState: this.audio.readyState,
        src: this.audio.src,
        currentVerse: this.currentVerse
      });
    },
    
    preloadNextVerse: function() {
      if (!verseQueueRef.current) return;
      
      const nextVerse = verseQueueRef.current.getNextVerse();
      if (nextVerse && this.nextAudio) {
        console.log(`🎵 VERSE PLAYER: Preloading next verse ${nextVerse.verseNumber}`);
        this.nextVerse = nextVerse;
        this.nextAudio.src = nextVerse.audioFile;
        this.nextAudio.load();
        // Pre-compute trim bounds for the next verse so the transition stays seamless.
        if (trimEnabledRef.current) prewarmTrim(nextVerse.audioFile);
      }
    },
    
    handleVerseEnd: function() {
      if (!this.currentVerse) return;
      
      console.log(`🎵 VERSE PLAYER: Verse ${this.currentVerse.verseNumber} ended, requesting next verse from queue`);
      
      // Notify verseQueue to advance
      if (verseQueueRef.current) {
        const hasNext = verseQueueRef.current.advanceToNext();
        if (hasNext) {
          const nextVerse = verseQueueRef.current.getCurrentVerse();
          if (nextVerse) {
            console.log(`📋 VERSE QUEUE: Sending next verse ${nextVerse.verseNumber} to versePlayer`);
            console.log(`🎵 VERSE PLAYER: Received next verse ${nextVerse.verseNumber} from queue, about to play`);
            
            // If we have a preloaded verse, use it for faster transition.
            // Skip the swap when enhancement is active — the MediaElementSource
            // is bound to this.audio and cannot follow an element swap.
            if (this.nextVerse && this.nextVerse.id === nextVerse.id && this.nextAudio && !mediaSourceCreatedRef.current) {
              console.log(`🎵 VERSE PLAYER: Using preloaded audio for faster transition`);
              // Swap audio elements
              const tempAudio = this.audio;
              this.audio = this.nextAudio;
              this.nextAudio = tempAudio;
              this.nextAudio.src = '';
              this.nextVerse = null;
            }
            
            // Load the next verse and highlight it
            // Auto-play if we're in continuous playback mode (user clicked main play button)
            this.loadVerse(nextVerse, true);
            verseHighlighterRef.current?.highlightVerse(nextVerse);
            
            // Play will be called automatically when the new audio is ready (in loadVerse)
          }
        } else {
          // No more verses in this queue — stop, and let the caller decide
          // whether to advance to the next plan item (chapter/passage) and
          // hand us a new queue. Distinct from a manual pause/stop, which
          // never fires this callback.
          console.log(`🎵 VERSE PLAYER: No more verses, stopping playback`);
          this.stop();
          onPlaybackStateChange?.(false, false);
          onQueueEnded?.();
        }
      }
    },
    
    loadVerse: function(verse, shouldAutoPlay = false) {
      if (!this.audio || !verse) return;

      // If already on this verse, just restart from the beginning
      if (this.currentVerse && this.currentVerse.id === verse.id && this.audio.src === verse.audioFile) {
        if (shouldAutoPlay) this.play();
        return;
      }
      
      this.currentVerse = verse;
      
      // Don't pause current audio until new audio is ready - this prevents cutting off
      // Store the current playing state
      const wasPlaying = !this.audio.paused;
      
      // Clear any existing sources
      this.audio.innerHTML = '';
      
      // Set the src directly as primary method
      this.audio.src = verse.audioFile;
      
      // Also create source element with proper MIME type as fallback
      const source = document.createElement('source');
      source.src = verse.audioFile;
      
      // Determine MIME type based on file extension
      if (verse.audioFile.includes('.wav')) {
        source.type = 'audio/wav';
      } else if (verse.audioFile.includes('.m4a') || verse.audioFile.includes('.mp4')) {
        source.type = 'audio/mp4';
      } else if (verse.audioFile.includes('.mp3')) {
        source.type = 'audio/mpeg';
      } else {
        source.type = 'audio/mp4'; // Default fallback
      }
      
      this.audio.appendChild(source);
      
      // Load the new audio and wait for it to be ready before pausing old audio
      this.audio.load();
      
      // Wait for the new audio to be ready before transitioning
      const handleCanPlay = () => {
        // Now pause the old audio and set up the new one
        this.audio.pause();
        this.audio.currentTime = 0;
        
        // Reset transition flag
        this.isTransitioning = false;
        
        // Remove the temporary event listener
        this.audio.removeEventListener('canplay', handleCanPlay);
        
        // Ensure event listeners are set up
        if (!this.boundHandlers) {
          this.setupEventListeners();
        } else {
          // Re-add the timeupdate listener after loading new audio
          this.audio.addEventListener('timeupdate', this.boundHandlers.timeupdate);
        }
        
        // Auto-play if currently playing
        if (shouldAutoPlay || wasPlaying) {
          this.play();
        }
      };
      
      // Add temporary listener for when new audio is ready
      this.audio.addEventListener('canplay', handleCanPlay);
    },
    
    play: function() {
      if (!this.audio || !this.currentVerse) return;

      // Ensure event listeners are set up before playing
      if (!this.boundHandlers) {
        this.setupEventListeners();
      }

      // Only route through Web Audio when Enhance Audio is on — plain
      // <audio> playback otherwise, so a Web Audio failure can never take
      // down playback for users who haven't opted in. Connects at most once
      // per component lifetime, here at the top of play() where the element
      // is guaranteed to be in a clean, not-yet-playing state (never hot-
      // connect a live/playing element — unreliable across browsers).
      if (enhanceAudioRef.current && !mediaSourceCreatedRef.current) {
        try {
          setupEnhancement(this.audio);
        } catch (error) {
          console.warn('🎵 Enhance Audio: setup failed, continuing with unenhanced playback', error);
        }
      }
      if (audioContextRef.current) {
        audioContextRef.current.resume().catch(() => {});
      }

      // Default: play the whole single-verse file from the start.
      this.trimEnd = null;
      this.audio.currentTime = 0;

      // Trim-silence on: skip leading dead air and stop at the spoken end.
      if (trimEnabledRef.current && this.currentVerse) {
        const verse = this.currentVerse;
        const cached = getCachedTrim(verse.audioFile);
        if (cached) {
          this.trimEnd = cached.end;
          if (cached.start > 0) this.audio.currentTime = cached.start;
        } else {
          // Not analyzed yet — start now, then jump forward once it resolves.
          analyzeTrim(verse.audioFile).then((t) => {
            if (!t || !trimEnabledRef.current) return;
            if (this.currentVerse !== verse || !this.audio) return;
            this.trimEnd = t.end;
            if (t.start > 0 && this.audio.currentTime < t.start) {
              this.audio.currentTime = t.start;
            }
          });
        }
      }

      this.audio.playbackRate = rateRef.current;
      this.audio.play().then(() => {
        this.isPlaying = true;
        setIsPlaying(true);
        onPlaybackStateChange?.(true, true);
      }).catch((error) => {
        console.error('🚨 Playback error:', error);
        this.stop();
        onPlaybackStateChange?.(false, false);
      });
    },
    
    pause: function() {
      if (!this.audio) return;
      
      this.audio.pause();
      this.isPlaying = false;
      setIsPlaying(false);
      onPlaybackStateChange?.(false, false);
    },
    
    stop: function() {
      if (!this.audio) return;
      
      this.audio.pause();
      this.audio.currentTime = 0;
      this.isPlaying = false;
      setIsPlaying(false);
      onPlaybackStateChange?.(false, false);
    },
    
    cleanup: function() {
      console.log('🎵 VERSE PLAYER: cleanup() called');
      console.log('🎵 VERSE PLAYER: cleanup - this.audio exists:', !!this.audio);
      console.log('🎵 VERSE PLAYER: cleanup - this.boundHandlers exist:', !!this.boundHandlers);
      
      if (!this.audio || !this.boundHandlers) {
        console.log('🎵 VERSE PLAYER: cleanup - early return, no audio or boundHandlers');
        return;
      }
      
      // Remove all event listeners
      this.audio.removeEventListener('loadedmetadata', this.boundHandlers.loadedmetadata);
      this.audio.removeEventListener('canplay', this.boundHandlers.canplay);
      this.audio.removeEventListener('timeupdate', this.boundHandlers.timeupdate);
      this.audio.removeEventListener('ended', this.boundHandlers.ended);
      
      // Clean up next audio element
      if (this.nextAudio) {
        this.nextAudio.src = '';
        this.nextAudio = null;
      }
      this.nextVerse = null;
      
      console.log('🎵 VERSE PLAYER: cleanup - setting boundHandlers to null');
      this.boundHandlers = null;
    }
  });

  // VerseHighlighter System - Manages UI highlighting
  const createVerseHighlighter = () => ({
    currentVerse: null,
    
    highlightVerse: function(verse) {
      if (!verse) return;
      
      this.currentVerse = verse;
      console.log(`🎯 VERSE HIGHLIGHTER: Highlighting verse ${verse.verseNumber}`);
      
      // Notify parent component
      onVersePlayback?.(verse.verseNumber);
    },
    
    clearHighlight: function() {
      this.currentVerse = null;
      onVersePlayback?.(null);
    }
  });

  // Track recordings to prevent unnecessary re-initializations
  const recordingsRef = useRef(null);
  
  // Initialize the three-component system
  useEffect(() => {
    console.log('📋 MULTI AUDIO PLAYER: useEffect triggered with recordings:', recordings?.length || 0);
    console.log('📋 MULTI AUDIO PLAYER: hasInitializedRef.current:', hasInitializedRef.current);
    console.log('📋 MULTI AUDIO PLAYER: recordingsRef.current:', recordingsRef.current?.length || 0);
    
    // Only initialize if we have recordings AND they appear to be real data (not empty arrays from initial state)
    if (recordings && recordings.length > 0 && recordings[0] && recordings[0].id) {
      // Check if recordings have actually changed
      const recordingsChanged = !recordingsRef.current || 
        recordingsRef.current.length !== recordings.length ||
        recordingsRef.current.some((rec, index) => 
          !recordings[index] || rec.id !== recordings[index].id
        );
      
      if (recordingsChanged) {
        console.log('📋 MULTI AUDIO PLAYER: Initializing with stable recordings data:', recordings.length);
        recordingsRef.current = recordings;

        // Capture playback state before tearing down so a mid-play filter
        // change continues playing from the same spot in the new queue.
        // autoPlayNewQueue forces this even though playback already
        // stopped (queue-end already paused the element) — set by the
        // caller right before advancing to a plan's next chapter/passage
        // during continuous playback.
        const wasPlaying = (hasInitializedRef.current && audioRef.current && !audioRef.current.paused) || autoPlayNewQueue;
        const priorVerse = versePlayerRef.current?.currentVerse || null;
        // Only resume position for a same-passage change (e.g. reader filter);
        // a new book/chapter starts from its first verse.
        const samePassage = priorVerse &&
          recordings[0].book === priorVerse.book &&
          recordings[0].chapter === priorVerse.chapter;
        const priorVerseNumber = samePassage ? priorVerse.verseNumber : null;

        // Stop current playback and cleanup if already initialized
        if (hasInitializedRef.current && versePlayerRef.current) {
          console.log('📋 MULTI AUDIO PLAYER: Re-initializing - stopping and cleaning up existing player');
          versePlayerRef.current.stop();
          versePlayerRef.current.cleanup();
        }

        // Create the three systems
        verseQueueRef.current = createVerseQueue();
        versePlayerRef.current = createVersePlayer();
        verseHighlighterRef.current = createVerseHighlighter();

        // Initialize verseQueue with recordings
        verseQueueRef.current.initialize(recordings);

        // Initialize versePlayer with audio element
        if (audioRef.current) {
          versePlayerRef.current.initialize(audioRef.current);

          // Resume at the same verse (or the next available one) if we were playing
          if (priorVerseNumber !== null) {
            const resumeIdx = verseQueueRef.current.allVerses.findIndex(
              v => v.verseNumber >= priorVerseNumber
            );
            if (resumeIdx > 0) {
              verseQueueRef.current.currentIndex = resumeIdx;
              verseQueueRef.current.remainingCount = verseQueueRef.current.allVerses.length - resumeIdx;
            }
          }

          const firstVerse = verseQueueRef.current.getCurrentVerse();
          if (firstVerse) {
            versePlayerRef.current.loadVerse(firstVerse, wasPlaying);
            verseHighlighterRef.current.highlightVerse(firstVerse);

            // Generate queue report
            verseQueueRef.current.generateQueueReport();
          }
        }

        hasInitializedRef.current = true;
        console.log('📋 MULTI AUDIO PLAYER: Initialization complete - hasInitializedRef set to true');
    } else {
        console.log('📋 MULTI AUDIO PLAYER: Recordings unchanged, skipping re-initialization');
      }
    } else if (recordings && recordings.length === 0) {
      // Clear any existing initialization when recordings become empty
      if (hasInitializedRef.current) {
        console.log('📋 MULTI AUDIO PLAYER: Clearing initialization - no recordings available');
        if (versePlayerRef.current) {
          versePlayerRef.current.stop();
          versePlayerRef.current.cleanup();
        }
        hasInitializedRef.current = false;
        recordingsRef.current = null;
      }
    }
  }, [recordings]);

  // Builds the AudioContext + filter chain and connects the given audio
  // element into it, exactly once. Only ever called from play(), never from
  // a toggle handler, so the element is always in a clean, not-yet-playing
  // state when createMediaElementSource() captures it.
  const setupEnhancement = (audioElement) => {
    if (mediaSourceCreatedRef.current) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    ctx.resume().catch(() => {});

    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 80;

    const peaking = ctx.createBiquadFilter();
    peaking.type = 'peaking';
    peaking.frequency.value = 2500;
    peaking.Q.value = 1.0;
    peaking.gain.value = 4;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.ratio.value = 4;
    compressor.knee.value = 30;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    const gainNode = ctx.createGain();
    gainNode.gain.value = 1.4;

    // Brick-wall limiter — the compressor's makeup gain plus the fixed 1.4x
    // boost above can push an already-loud verse past 0dBFS with nothing to
    // catch it, which the browser/OS audio output hard-clips (heard as random
    // dropouts, worse on louder passages). A fast, high-ratio compressor right
    // before destination catches those transients without audibly coloring
    // anything that wasn't already close to the ceiling.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;

    highpass.connect(peaking);
    peaking.connect(compressor);
    compressor.connect(gainNode);
    gainNode.connect(limiter);
    limiter.connect(ctx.destination);

    const source = ctx.createMediaElementSource(audioElement);
    source.connect(highpass);

    audioContextRef.current = ctx;
    mediaSourceCreatedRef.current = true;
  };

  // Keep the live trim flag in sync, and pre-warm analysis for the verse(s)
  // we're about to need so trimming applies without a hitch.
  useEffect(() => {
    trimEnabledRef.current = !!trimSilence;
    if (!trimSilence) return;
    const current = versePlayerRef.current?.currentVerse;
    if (current?.audioFile) prewarmTrim(current.audioFile);
    const next = verseQueueRef.current?.getNextVerse?.();
    if (next?.audioFile) prewarmTrim(next.audioFile);
  }, [trimSilence]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    playAll: () => {
      if (!versePlayerRef.current) return;

      if (isPlaying) {
        versePlayerRef.current.pause();
        setIsPlaying(false);
      } else {
        versePlayerRef.current.play();
        setIsPlaying(true);
      }
    },

    pause: () => {
      if (!versePlayerRef.current) return;
      versePlayerRef.current.pause();
      setIsPlaying(false);
    },

    stop: () => {
      if (!versePlayerRef.current) return;
      versePlayerRef.current.stop();
      setIsPlaying(false);
    },

    playFromVerse: (verseNumber) => {
      if (!verseQueueRef.current || !versePlayerRef.current) return;
      const allVerses = verseQueueRef.current.allVerses;
      // Exact match, or nearest recorded verse at or after the tapped one
      let idx = allVerses.findIndex(v => v.verseNumber === verseNumber);
      if (idx === -1) idx = allVerses.findIndex(v => v.verseNumber > verseNumber);
      if (idx === -1) return;
      verseQueueRef.current.currentIndex = idx;
      verseQueueRef.current.remainingCount = allVerses.length - idx;
      const verse = allVerses[idx];
      versePlayerRef.current.loadVerse(verse, true);
      verseHighlighterRef.current?.highlightVerse(verse);
    },

    getButtonState: () => {
      return {
        isPlaying: isPlaying,
        buttonText: isPlaying ? 'Pause' : 'Play',
        buttonClass: 'bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white'
      };
    },

    cleanup: () => {
      if (!versePlayerRef.current) return;
      versePlayerRef.current.cleanup();
      setIsPlaying(false);
    }
  }), [isPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (versePlayerRef.current) {
        versePlayerRef.current.stop();
        versePlayerRef.current.cleanup();
      }
    };
  }, []);

  // Determine if we're in a loading state
  const isLoading = !recordings || recordings.length === 0 || !recordings[0] || !recordings[0].id;
  const hasRecordings = recordings && recordings.length > 0 && recordings[0] && recordings[0].id;

  // Notify parent component of loading state changes
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoading);
    }
  }, [isLoading, onLoadingStateChange]);

  return (
    <div className="relative">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="metadata"
        crossOrigin="anonymous"
        style={{ display: 'none' }}
      />
    </div>
  );
});

MultiAudioPlayer.displayName = 'MultiAudioPlayer';

export default MultiAudioPlayer;
